import { z, ZodTypeDef } from 'zod';
import { transformAndFilterValidItems } from '../../../common/tools/transformUtils';
import { ImageSchema } from '../../../common/schemas/imageZodSchemas';

// Pimcore field-collection schemas

export const ExternalLinkElementFieldCollectionSchema = z.object({
    __typename: z.literal('fieldcollection_ExternalLinkElement'),
    linkText: z.string(),
    linkUrl: z.string(),
});

export const InternalLinkElementFieldCollectionSchema = z.object({
    __typename: z.literal('fieldcollection_InternalLinkElement'),
    linkText: z.string(),
    linkRoute: z.string(),
});

export const ExternalImageLinkElementFieldCollectionSchema = z.object({
    __typename: z.literal('fieldcollection_ExternalImageLinkElement'),
    tooltip: z.string().nullable(),
    linkUrl: z.string(),
    image: ImageSchema,
});

export const InternalImageLinkElementFieldCollectionSchema = z.object({
    __typename: z.literal('fieldcollection_InternalImageLinkElement'),
    tooltip: z.string().nullable(),
    linkRoute: z.string(),
    image: ImageSchema,
});

export const ResourceLibraryItemLinkElementFieldCollectionSchema = z.object({
    __typename: z.literal('fieldcollection_ResourceLibraryItemLinkElement'),
    linkText: z.string().nullable(),
    resourceLibraryItem: z.object({
        id: z.string(),
        name: z.string(),
    }),
});

export const ContentSectionElementFieldCollectionSchema = z.object({
    __typename: z.literal('fieldcollection_ContentSectionElement'),
    title: z.string(),
    content: z.string(),
});

// Pimcore classes schemas

/** Schema for ExternalLink element (ExternalLinkElement class) */
export const ExternalLinkElementSchema = z
    .object({
        __typename: z.literal('object_ExternalLinkElement'),
        key: z.string(),
        linkText: z.string(),
        linkUrl: z.string(),
    })
    .describe('ExternalLinkElementSchema');

/** Schema for ExternalLink optional element (ExternalLinkElementOptional class) */
export const ExternalLinkElementOptionalSchema = z
    .object({
        __typename: z.literal('object_ExternalLinkElementOptional'),
        key: z.string(),
        linkText: z.string().nullable(),
        linkUrl: z.string().nullable(),
    })
    .describe('ExternalLinkElementOptionalSchema');

/** Schema for InternalLink element (InternalLinkElement class) */
export const InternalLinkElementSchema = z
    .object({
        __typename: z.literal('object_InternalLinkElement'),
        key: z.string(),
        linkText: z.string(),
        linkRoute: z.string(),
    })
    .describe('InternalLinkElementSchema');

/** Schema for InternalLink optional element (InternalLinkElementOptional class) */
export const InternalLinkElementOptionalSchema = z
    .object({
        __typename: z.literal('object_InternalLinkElementOptional'),
        key: z.string(),
        linkText: z.string().nullable(),
        linkRoute: z.string().nullable(),
    })
    .describe('InternalLinkElementOptionalSchema');

/** Entry for LinkCollection: external link (ExternalLinkElement FieldCollection) */
export const LinkCollectionEntryExternalSchema = ExternalLinkElementFieldCollectionSchema.describe(
    'LinkCollectionEntryExternalSchema'
);

/** Entry for LinkCollection: internal link (InternalLinkElement FieldCollection)*/
export const LinkCollectionEntryInternalSchema = InternalLinkElementFieldCollectionSchema.describe(
    'LinkCollectionEntryInternalSchema'
);

/** Union of LinkCollection entry schemas */
export const LinkCollectionEntrySchema = z
    .union([LinkCollectionEntryExternalSchema, LinkCollectionEntryInternalSchema])
    .describe('LinkCollectionEntrySchema');

/** Schema for LinkCollection element (LinkCollectionElement class) */
export const LinkCollectionElementSchema = z
    .object({
        __typename: z.literal('object_LinkCollectionElement'),
        key: z.string(),
        links: z
            .array(z.unknown())
            .transform((links) => transformAndFilterValidItems(LinkCollectionEntrySchema, links)),
    })
    .describe('LinkCollectionElementSchema');

export const InternalLinkSectionEntrySchema = z
    .union([
        InternalLinkElementFieldCollectionSchema,
        ResourceLibraryItemLinkElementFieldCollectionSchema,
    ])
    .describe('InternalLinkSectionEntrySchema');

export const InternalLinkSectionElementSchema = z
    .object({
        __typename: z.literal('object_InternalLinkSectionElement'),
        key: z.string(),
        title: z.string(),
        text: z.string().nullable(),
        links: z
            .array(z.unknown())
            .transform((links) =>
                transformAndFilterValidItems(InternalLinkSectionEntrySchema, links)
            ),
    })
    .describe('InternalLinkSectionElementSchema');

/** Entry for ImageLinkCollection: external image link (ExternalImageLinkElement FieldCollection) */
export const ImageLinkCollectionEntryExternalSchema =
    ExternalImageLinkElementFieldCollectionSchema.describe(
        'ImageLinkCollectionEntryExternalSchema'
    );

/** Entry for ImageLinkCollection: internal image link (InternalImageLinkElement FieldCollection)*/
export const ImageLinkCollectionEntryInternalSchema =
    InternalImageLinkElementFieldCollectionSchema.describe(
        'ImageLinkCollectionEntryInternalSchema'
    );

/** Union of ImageLinkCollection entry schemas */
export const ImageLinkCollectionEntrySchema = z
    .union([ImageLinkCollectionEntryExternalSchema, ImageLinkCollectionEntryInternalSchema])
    .describe('ImageLinkCollectionEntrySchema');

/** Schema for ImageLinkCollection element (ImageLinkCollectionElement class) */
export const ImageLinkCollectionElementSchema = z
    .object({
        __typename: z.literal('object_ImageLinkCollectionElement'),
        key: z.string(),
        links: z
            .array(z.unknown())
            .transform((links) =>
                transformAndFilterValidItems(ImageLinkCollectionEntrySchema, links)
            ),
    })
    .describe('ImageLinkCollectionElementSchema');

/** Schema for Image element (ImageElement class) */
export const ImageElementSchema = z
    .object({
        __typename: z.literal('object_ImageElement'),
        key: z.string(),
        image: z.object({ fullpath: z.string() }),
    })
    .describe('ImageElementSchema');

/** Schema for Image optional element (ImageElementOptional class) */
export const ImageElementOptionalSchema = z
    .object({
        __typename: z.literal('object_ImageElementOptional'),
        key: z.string(),
        image: z.object({ fullpath: z.string() }).nullable(),
    })
    .describe('ImageElementOptionalSchema');

/** Schema for PlainText element (PlainTextElement class) */
export const PlainTextElementSchema = z
    .object({
        __typename: z.literal('object_PlainTextElement'),
        key: z.string(),
        content: z.string(),
    })
    .describe('PlainTextElementSchema');

/** Schema for PlainText optional element (PlainTextElementOptional class) */
export const PlainTextElementOptionalSchema = z
    .object({
        __typename: z.literal('object_PlainTextElementOptional'),
        key: z.string(),
        content: z.string().nullable(),
    })
    .describe('PlainTextElementOptionalSchema');

/** Schema for PlainMultilineText element (PlainMultilineTextElement class) */
export const PlainMultilineTextElementSchema = z
    .object({
        __typename: z.literal('object_PlainMultilineTextElement'),
        key: z.string(),
        content: z.string(),
    })
    .describe('PlainMultilineTextElementSchema');

/** Schema for PlainMultilineText optional element (PlainMultilineTextElementOptional class) */
export const PlainMultilineTextElementOptionalSchema = z
    .object({
        __typename: z.literal('object_PlainMultilineTextElementOptional'),
        key: z.string(),
        content: z.string().nullable(),
    })
    .describe('PlainMultilineTextElementOptionalSchema');

/** Schema for RichText element (RichTextElement class) */
export const RichTextElementSchema = z
    .object({
        __typename: z.literal('object_RichTextElement'),
        key: z.string(),
        content: z.string(),
    })
    .describe('RichTextElementSchema');

/** Schema for RichText optional element (RichTextElementOptional class) */
export const RichTextElementOptionalSchema = z
    .object({
        __typename: z.literal('object_RichTextElementOptional'),
        key: z.string(),
        content: z.string().nullable(),
    })
    .describe('RichTextElementOptionalSchema');

/** Schema for Table element (TableElement class) */
export const TableElementSchema = z
    .object({
        __typename: z.literal('object_TableElement'),
        key: z.string(),
        tableHeader: z.string(),
        tableBody: z.string(),
    })
    .describe('TableElementSchema');

/** Entry for PartitionedTable (PartitionTableEntry FieldCollection) */
export const PartitionedTableEntrySchema = z
    .object({
        selectionKey: z.string(),
        tableHeader: z.string().nullable(),
        tableBody: z.string(),
    })
    .describe('PartitionedTableEntrySchema');

/** Schema for PartitionedTable element (PartitionedTableElement class) */
export const PartitionedTableElementSchema = z
    .object({
        __typename: z.literal('object_PartitionedTableElement'),
        key: z.string(),
        commonTableHeader: z.string(),
        tablePartitions: z
            .array(z.unknown())
            .transform((parts) => transformAndFilterValidItems(PartitionedTableEntrySchema, parts)),
    })
    .describe('PartitionedTableElementSchema');

/** Entry for HighlightsCollection element (HighlightsCollectionEntry FieldCollection)*/
export const HighlightsCollectionEntrySchema = z
    .object({
        title: z.string(),
        image: z.object({ fullpath: z.string() }),
        text: z.string(),
    })
    .describe('HighlightsCollectionEntrySchema');

/** Schema for HighlightsCollection element (HighlightsCollectionElement class) */
export const HighlightsCollectionElementSchema = z
    .object({
        __typename: z.literal('object_HighlightsCollectionElement'),
        key: z.string(),
        entries: z
            .array(z.unknown())
            .transform((entries) =>
                transformAndFilterValidItems(HighlightsCollectionEntrySchema, entries)
            ),
    })
    .describe('HighlightsCollectionElementSchema');

/** Schema for GovernanceRole person profile (PersonProfileElement FieldCollection) */
export const GovernanceRolePersonProfileSchema = z
    .object({ name: z.string(), content: z.string() })
    .describe('GovernanceRolePersonProfileSchema');

/** Schema for GovernanceRole element */
export const GovernanceRoleElementSchema = z
    .object({
        __typename: z.literal('object_GovernanceRoleElement'),
        key: z.string(),
        title: z.string(),
        personProfiles: z
            .array(z.unknown())
            .transform((profiles) =>
                transformAndFilterValidItems(GovernanceRolePersonProfileSchema, profiles)
            ),
    })
    .describe('GovernanceRoleElementSchema');

/** Entry for ProductApplicationSegmentCard fieldCollection (ProductApplicationSegmentCard FieldCollection) */
export const ProductApplicationSegmentCard = z
    .object({
        image: z.object({ fullpath: z.string() }),
        productApplicationSegment: z.object({ name: z.string() }),
        route: z.string(),
    })
    .describe('ProductApplicationSegmentCard');

/** Schema for ProductApplicationSegmentCardCollection element (ProductApplicationSegmentCardCollectionElement class) */
export const ProductApplicationSegmentCardCollectionElementSchema = z
    .object({
        __typename: z.literal('object_ProductApplicationSegmentCardCollectionElement'),
        key: z.string(),
        entries: z
            .array(z.unknown())
            .transform((entries) =>
                transformAndFilterValidItems(ProductApplicationSegmentCard, entries)
            ),
    })
    .describe('ProductApplicationSegmentCardCollectionElementSchema');

/** Entry for BrandCard fieldCollection (BrandCard FieldCollection) */
export const BrandCard = z
    .object({
        brand: z.object({
            id: z.string(),
            name: z.string(),
            logo: z.object({ fullpath: z.string() }).nullable(),
        }),
        text: z.string(),
        externalLinkUrl: z.string().nullable(),
    })
    .describe('BrandCard');

/** Schema for BrandCardCollection element (BrandCardCollectionElement class) */
export const BrandCardCollectionElementSchema = z
    .object({
        __typename: z.literal('object_BrandCardCollectionElement'),
        key: z.string(),
        entries: z
            .array(z.unknown())
            .transform((entries) => transformAndFilterValidItems(BrandCard, entries)),
    })
    .describe('BrandCardCollectionElementSchema');

// CompanyFocusTopic schema

/** Entry for CompanyFocusTopicElement: content section (ContentSectionElement FieldCollection) */
export const CompanyFocusTopicEntryContentSectionchema =
    ContentSectionElementFieldCollectionSchema.describe(
        'CompanyFocusTopicEntryContentSectionchema'
    );

/** Entry for CompanyFocusTopicElement: external link (ExternalLinkElement FieldCollection) */
export const CompanyFocusTopicEntryExternalSchema =
    ExternalLinkElementFieldCollectionSchema.describe('CompanyFocusTopicEntryExternalSchema');

/** Entry for CompanyFocusTopicElement: internal link (InternalLinkElement FieldCollection) */
export const CompanyFocusTopicEntryInternalSchema =
    InternalLinkElementFieldCollectionSchema.describe('CompanyFocusTopicEntryInternalSchema');

/** Union of CompanyFocusTopicElement entries */
export const CompanyFocusTopicElementEntrySchema = z
    .union([
        CompanyFocusTopicEntryContentSectionchema,
        CompanyFocusTopicEntryExternalSchema,
        CompanyFocusTopicEntryInternalSchema,
    ])
    .describe('CompanyFocusTopicElementEntrySchema');

// infoBannerCollectionElement

/** Entry for InfoBannerCollectionElement (InfoBannerCollectionEntry FieldCollection) */
export const InfoBannerCollectionEntrySchema = z
    .object({
        identifier: z.string(),
        content: z.string(),
        enabled: z.boolean(),
    })
    .describe('InfoBannerCollectionEntrySchema');

/** Schema for InfoBannerCollectionElement (InfoBannerCollectionElement class) */
export const InfoBannerCollectionElementSchema = z
    .object({
        __typename: z.literal('object_InfoBannerCollectionElement'),
        id: z.string(),
        key: z.string(),
        entries: z
            .array(z.unknown())
            .transform((entries) =>
                transformAndFilterValidItems(InfoBannerCollectionEntrySchema, entries)
            ),
    })
    .describe('InfoBannerCollectionElementSchema');

/** Schema for CompanyFocusTopicElement (CompanyFocusTopicElement class) */
export const CompanyFocusTopicElementSchema = z
    .object({
        __typename: z.literal('object_CompanyFocusTopicElement'),
        key: z.string(),
        title: z.string().nullable(),
        entries: z
            .array(z.unknown())
            .transform((entries) =>
                transformAndFilterValidItems(CompanyFocusTopicElementEntrySchema, entries)
            ),
    })
    .describe('CompanyFocusTopicElementSchema');

// without folders, just the leaf elements
export const SiteContentElementSchema = z.discriminatedUnion('__typename', [
    ExternalLinkElementSchema,
    ExternalLinkElementOptionalSchema,
    InternalLinkElementSchema,
    InternalLinkElementOptionalSchema,
    LinkCollectionElementSchema,
    InternalLinkSectionElementSchema,
    ImageLinkCollectionElementSchema,
    ImageElementSchema,
    ImageElementOptionalSchema,
    PlainTextElementSchema,
    PlainTextElementOptionalSchema,
    PlainMultilineTextElementSchema,
    PlainMultilineTextElementOptionalSchema,
    RichTextElementSchema,
    RichTextElementOptionalSchema,
    TableElementSchema,
    PartitionedTableElementSchema,
    HighlightsCollectionElementSchema,
    GovernanceRoleElementSchema,
    ProductApplicationSegmentCardCollectionElementSchema,
    BrandCardCollectionElementSchema,
    CompanyFocusTopicElementSchema,
    InfoBannerCollectionElementSchema,
]);

/** Union of all site content elements - discriminated by __typename, including folders */
type SiteContentNode =
    | z.infer<typeof ExternalLinkElementSchema>
    | z.infer<typeof ExternalLinkElementOptionalSchema>
    | z.infer<typeof InternalLinkElementSchema>
    | z.infer<typeof InternalLinkElementOptionalSchema>
    | z.infer<typeof ImageLinkCollectionElementSchema>
    | z.infer<typeof LinkCollectionElementSchema>
    | z.infer<typeof InternalLinkSectionElementSchema>
    | z.infer<typeof ImageElementSchema>
    | z.infer<typeof ImageElementOptionalSchema>
    | z.infer<typeof PlainTextElementSchema>
    | z.infer<typeof PlainTextElementOptionalSchema>
    | z.infer<typeof PlainMultilineTextElementSchema>
    | z.infer<typeof PlainMultilineTextElementOptionalSchema>
    | z.infer<typeof RichTextElementSchema>
    | z.infer<typeof RichTextElementOptionalSchema>
    | z.infer<typeof TableElementSchema>
    | z.infer<typeof PartitionedTableElementSchema>
    | z.infer<typeof HighlightsCollectionElementSchema>
    | z.infer<typeof GovernanceRoleElementSchema>
    | z.infer<typeof ProductApplicationSegmentCardCollectionElementSchema>
    | z.infer<typeof BrandCardCollectionElementSchema>
    | z.infer<typeof CompanyFocusTopicElementSchema>
    | z.infer<typeof InfoBannerCollectionElementSchema>
    | ElementReference
    | { __typename: 'object_folder'; key: string; children: SiteContentNode[] };

/** Schema for all site content nodes, including folders */
export const SiteContentNodeSchema: z.ZodSchema<SiteContentNode, ZodTypeDef, unknown> = z.lazy(() =>
    z.discriminatedUnion('__typename', [
        ExternalLinkElementSchema,
        ExternalLinkElementOptionalSchema,
        InternalLinkElementSchema,
        InternalLinkElementOptionalSchema,
        LinkCollectionElementSchema,
        InternalLinkSectionElementSchema,
        ImageLinkCollectionElementSchema,
        ImageElementSchema,
        ImageElementOptionalSchema,
        PlainTextElementSchema,
        PlainTextElementOptionalSchema,
        PlainMultilineTextElementSchema,
        PlainMultilineTextElementOptionalSchema,
        RichTextElementSchema,
        RichTextElementOptionalSchema,
        TableElementSchema,
        PartitionedTableElementSchema,
        HighlightsCollectionElementSchema,
        GovernanceRoleElementSchema,
        ProductApplicationSegmentCardCollectionElementSchema,
        BrandCardCollectionElementSchema,
        CompanyFocusTopicElementSchema,
        ElementReferenceSchema,
        InfoBannerCollectionElementSchema,
        /** Insert the folder schema last, pointing back at this union */
        z
            .object({
                __typename: z.literal('object_folder'),
                key: z.string(),
                children: z
                    .array(z.unknown())
                    .transform((xs) => transformAndFilterValidItems(SiteContentNodeSchema, xs)),
            })
            .describe('FolderElementSchema'),
    ])
);

/** Schema for ElementReference, which is a reference to a single leaf element */
const ElementReferenceNestedSchema = z
    .object({
        __typename: z.literal('object_ElementReference'),
        key: z.string(),
        reference: SiteContentElementSchema,
    })
    .describe('ElementReferenceNestedSchema');

export const ElementReferenceSchema = z
    .object({
        __typename: z.literal('object_ElementReference'),
        key: z.string(),
        // only leaf elements are valid here, folders are not allowed
        reference: z.union([SiteContentElementSchema, ElementReferenceNestedSchema]),
    })
    .describe('ElementReferenceSchema');

export type ElementReference = z.infer<typeof ElementReferenceSchema>;

const SiteContentListingItemSchema = z.union([SiteContentNodeSchema, ElementReferenceSchema]);

/** Main schema to validate getSiteContentElementListing response */
export const GetSiteContentElementListingSchema = z
    .object({
        getObjectFolder: z.object({
            children: z
                .array(z.unknown())
                .transform((children) =>
                    transformAndFilterValidItems(SiteContentListingItemSchema, children)
                ),
        }),
    })
    .describe('GetSiteContentElementListingSchema');
